import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderPaymentProofUrlItem } from "@/lib/api/types";
import { formatAmountValue, formatAbsoluteDate } from "./order-list.utils";

type Props = {
  locale: string;
  proofItems: OrderPaymentProofUrlItem[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export function PaymentReviewProofViewer({
  locale,
  proofItems,
  selectedIndex,
  onSelect,
}: Props) {
  const t = useTranslations();
  const selected = proofItems[selectedIndex] ?? null;

  if (!selected) {
    return (
      <div className="rounded-xl border border-dashed border-[color:var(--border-light)] p-4 text-sm text-[color:var(--text-muted)]">
        {t(keys.orders.noProofLabel)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative h-72 w-full overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] sm:h-80">
        <img
          src={selected.url}
          alt={t(keys.orders.proofUploadedLabel)}
          className="object-contain"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
      <div className="text-xs text-[color:var(--text-muted)]">
        <span>{formatAmountValue(locale, selected.paidAmount)}</span>
        <span className="mx-2">•</span>
        <span>{formatAbsoluteDate(locale, selected.uploadedAt)}</span>
      </div>
      {proofItems.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {proofItems.map((proof, index) => (
            <button
              key={proof.proofId}
              type="button"
              onClick={() => onSelect(index)}
              className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-md border ${
                index === selectedIndex
                  ? "border-[color:var(--primary-500)]"
                  : "border-[color:var(--border-light)]"
              }`}
            >
              <img
                src={proof.url}
                alt={t(keys.orders.proofUploadedLabel)}
                className="object-cover"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
